import { useFeature } from '@vp/core-ui-components';
import { useLineItemData } from '../contexts/LineItemDataContext';
import { LineItemActionsProvider } from '../contexts/LineItemActionsContext';
import { useCustomerFeedbackGroup } from '../contexts/CustomerFeedbackGroupsContext';
import {
  FeedbackGroupState,
  FeedbackType,
} from '../../constants/customerFeedback';
import { useOrderSummary } from '../contexts/OrderSummaryContext';

type LineItemActionsWrapperProps = {
  children: React.ReactNode;
};

/**
 * This wrapper  determines which actions are enabled for a line item.
 */
export const LineItemActionsWrapper = ({
  children,
}: LineItemActionsWrapperProps) => {
  const item = useLineItemData();
  const {
    deliverability: { itemDeliverable: isDeliverable },
    actions: { reorder: isReorderable },
    editDocumentUrl,
    fulfillmentHistory,
    id,
    isGroupItem,
  } = item;

  const expressCheckoutEnabled = useFeature(
    'enableExpressCheckoutOnOrderHistory'
  );

  const orderSummary = useOrderSummary();

  // Always allow reorder for any deliverable product.
  // If a legacy product is not reorderable (does not have a new platform mapping) we still render
  // the button and pop a modal / fire a metric upon reorder attempt.
  // See {@link https://gitlab.com/vistaprint-org/order-management/orders/orderdetailsui/-/merge_requests/502}

  const reorderActionEnabled = isDeliverable;

  // Only show buy Now button if all reorder criteria is met, and feature is enabled
  // We still show buyNow button for items with `isReorderable: false`, because doc conversion may succeed on the reorder attempt
  const buyNowActionEnabled = expressCheckoutEnabled && reorderActionEnabled;

  // Enable design review on the item if it has an active customer feedback request
  // specific to document review and in a pending state
  const { customerFeedbackGroupExists: designFeedbackActionEnabled } =
    useCustomerFeedbackGroup({
      lineItemId: id as string, // fix, doing this b/c useCustomerFeedbackGroup now expects id to be not null/ undefined
      requestType: FeedbackType.DOCUMENT_REVIEW,
      groupState: FeedbackGroupState.AWAITING_FEEDBACK,
    });

  const fulfillmentHistoryFeatureEnabled = useFeature(
    'enableFulfillmentHistory'
  );

  const isMigratedOrder = orderSummary.order.migrated;

  // Only allow edit design and reorder option if reorder is enabled and the order is editable.
  // This means the item must be reorderable for migrated orders, or contain an edit document URL for new platform orders
  const editAndReorderActionEnabled =
    reorderActionEnabled &&
    (isMigratedOrder
      ? isReorderable
      : editDocumentUrl !== null && editDocumentUrl !== '');

  /**
   * This ensures that line items with fulfillment history display the new fulfillment tracking experience,
   * while line items that were placed before the feature rollout display the old experience, e.g. status text
   * with old design review feedback banner or cancellation tooltip when appropriate.
   * */
  const isFulfillmentHistoryEnabledForLineItem =
    fulfillmentHistoryFeatureEnabled &&
    (fulfillmentHistory?.fulfillments?.length ?? 0) > 0;

  const isReorderableProp = item.actions?.reorder;

  return (
    <LineItemActionsProvider
      buyNowActionEnabled={buyNowActionEnabled}
      designFeedbackActionEnabled={designFeedbackActionEnabled}
      editAndReorderActionEnabled={editAndReorderActionEnabled}
      fulfillmentHistoryEnabledForLineItem={
        isFulfillmentHistoryEnabledForLineItem || false
      }
      isGroupItem={isGroupItem}
      isReorderable={isReorderableProp}
      reorderActionEnabled={reorderActionEnabled}
    >
      {children}
    </LineItemActionsProvider>
  );
};
